import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { EventsMap } from "../components/EventsMap";
import { Loader } from "../components/Loader";
import { listPublicEvents, type PublicEventCard } from "../api/events";
import { formatStartsAt } from "../lib/format";

export function MapPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [events, setEvents] = useState<PublicEventCard[] | null>(null);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listPublicEvents()
      .then((res) => setEvents(res.events))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center px-6 text-center">
        <div className="text-[14px] text-brand-red">{t("mapPage.errorLoad", { error })}</div>
      </div>
    );
  }
  if (!events) return <Loader text={t("mapPage.loading")} full />;

  return (
    <div className="min-h-screen bg-surface">
      <div className="px-5 pt-6 pb-4 border-b border-ink-100 sticky top-0 z-10 bg-surface/95 backdrop-blur">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-[22px] font-extrabold text-ink-900 tracking-tight">
              {t("mapPage.title")}
            </h1>
            <p className="text-[13px] text-ink-500 mt-0.5">
              {t("mapPage.eventCount", { count: events.length })}
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="text-[13px] text-ink-500 hover:text-ink-900 underline underline-offset-4"
          >
            {t("mapPage.toHome")}
          </button>
        </div>
      </div>

      <div className="px-3 pt-3">
        <EventsMap
          events={events}
          height="60vh"
          focusedId={focusedId}
          onMarkerClick={(id) => setFocusedId(id)}
          onPopupCta={(id) => navigate(`/events/${id}`)}
        />
      </div>

      <div className="px-3 pt-4 pb-10 space-y-2">
        {events.map((e) => (
          <button
            key={e.id}
            type="button"
            onMouseEnter={() => setFocusedId(e.id)}
            onClick={() => setFocusedId(e.id)}
            className={`
              w-full text-left p-3 rounded-[var(--radius-md)] border transition-all
              ${focusedId === e.id
                ? "bg-brand-purple-soft border-brand-purple"
                : "bg-ink-50 border-ink-100 hover:border-ink-300"}
            `}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-[15px] font-bold text-ink-900 leading-tight truncate">{e.title}</div>
                <div className="text-[13px] text-ink-500 mt-0.5">{formatStartsAt(e.starts_at)}</div>
                <div className="text-[13px] text-ink-500 truncate">{e.venue_name}</div>
              </div>
              <div className="flex-shrink-0 flex flex-col items-end gap-1">
                {e.format && (
                  <span className="text-[11px] font-bold uppercase tracking-wider text-brand-purple">
                    {t(`labels.format.${e.format}`, { defaultValue: e.format })}
                  </span>
                )}
                <span className="text-[12px] text-ink-700">{t("mapPage.attendees", { count: e.going_count })}</span>
              </div>
            </div>
            <button
              type="button"
              onClick={(ev) => {
                ev.stopPropagation();
                navigate(`/events/${e.id}`);
              }}
              className="mt-2 text-[13px] font-semibold text-brand-purple"
            >
              {t("mapPage.open")}
            </button>
          </button>
        ))}
      </div>
    </div>
  );
}
